import React from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { quickFillActionTooltips } from '../../../../shared/config';
import { borderRadius, layoutGap } from '../../../../theme/theme';

export const VALUE_PANEL_TITLE_ID = 'quickfill-value-panel-title';
export const VALUE_PANEL_DESCRIPTION_ID = 'quickfill-value-panel-description';

export const ValueSelectorPanelHeader: React.FC<{
  columnLabel: string;
  selectedCount: number;
  currentValue?: string;
  filteredOptionsCount: number;
  totalUsed: number;
  isShortViewport: boolean;
  onClose: () => void;
}> = ({
  columnLabel,
  selectedCount,
  currentValue,
  filteredOptionsCount,
  totalUsed,
  isShortViewport,
  onClose,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        px: 1,
        py: isShortViewport ? 0.45 : 0.8,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense }}>
            <Typography
              id={VALUE_PANEL_TITLE_ID}
              variant="subtitle2"
              sx={{ fontWeight: 700, color: theme.palette.text.primary }}
            >
              {columnLabel}
            </Typography>
          </Box>
          <Typography
            id={VALUE_PANEL_DESCRIPTION_ID}
            variant="caption"
            color="text.secondary"
            sx={{ display: isShortViewport ? 'none' : 'block', mt: 0.55 }}
          >
            {selectedCount > 1
              ? `Fill ${selectedCount} selected cells with one click.`
              : 'Pick a value for the current cell.'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            aria-live="polite"
            sx={{ display: 'block', mt: isShortViewport ? 0.1 : 0.3 }}
          >
            {filteredOptionsCount} options{totalUsed > 0 ? ` / ${totalUsed} filled` : ''}
          </Typography>
        </Box>
        <ActionHelpTooltip help={quickFillActionTooltips.closePanel}>
          <IconButton
            size="small"
            aria-label="Close value selector panel"
            onClick={onClose}
            sx={{
              ml: 1,
              color: theme.palette.text.secondary,
              borderRadius: `${borderRadius.sm}px`,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </ActionHelpTooltip>
      </Box>

      {selectedCount === 1 && currentValue && (
        <Chip
          label={`Current: ${currentValue}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            mt: 0.6,
            maxWidth: '100%',
            borderRadius: `${borderRadius.none}px`,
            color: theme.palette.primary.light,
            backgroundColor: 'transparent',
          }}
        />
      )}
    </Box>
  );
};

export const ValueSelectorPanelFooter: React.FC<{
  typedValue: string;
  setTypedValue: (value: string) => void;
  onApply: () => void;
  onClearSelection: () => void;
  onClearColumn: () => void;
  selectedCount: number;
  isShortViewport: boolean;
}> = ({
  typedValue,
  setTypedValue,
  onApply,
  onClearSelection,
  onClearColumn,
  selectedCount,
  isShortViewport,
}) => {
  return (
    <Box
      sx={{
        px: 1,
        py: isShortViewport ? 0.5 : 0.75,
        borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.16)}`,
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={selectedCount > 1 ? 'Type a value for the selected cells' : 'Type a value for this cell'}
        inputProps={{ 'aria-label': 'Type custom value' }}
        value={typedValue}
        onChange={(event) => setTypedValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onApply();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button
                size="small"
                variant="contained"
                onClick={onApply}
                disabled={typedValue === ''}
                aria-label="Apply typed value"
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  py: layoutGap.micro,
                  borderRadius: `${borderRadius.none}px`,
                  boxShadow: 'none',
                }}
              >
                Apply
              </Button>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: isShortViewport ? 30 : 32,
            pr: layoutGap.dense,
            borderRadius: `${borderRadius.none}px`,
            backgroundColor: 'transparent',
          },
        }}
      />
      <Stack direction="row" spacing={layoutGap.dense} sx={{ mt: isShortViewport ? 0.5 : 0.75 }}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          color="warning"
          startIcon={<ClearIcon fontSize="small" />}
          onClick={onClearSelection}
          aria-label="Clear selected cells"
        >
          Clear cells
        </Button>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          color="warning"
          startIcon={<DeleteSweepIcon fontSize="small" />}
          onClick={onClearColumn}
          aria-label="Clear active column"
        >
          Clear column
        </Button>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: isShortViewport ? 'none' : 'block', mt: 0.45 }}
      >
        Type any custom value and apply it to {selectedCount > 1 ? 'all selected cells' : 'the active cell'}.
      </Typography>

      <Box sx={{ mt: isShortViewport ? 0.5 : 0.75, display: isShortViewport ? 'none' : 'block' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
          Click once to fill {selectedCount > 1 ? 'the current multi-selection' : 'the selected cell'}.
          Search narrows the list instantly.
        </Typography>
      </Box>
    </Box>
  );
};
